import axios from 'axios'
import config from '../../../../config'
import BizinBackend from '../../../../helpers/bizin_helper'
import {
  escapeHtml,
  renderExportTableCellHtml,
  buildReportHeaderHtml,
  buildFiltersBarHtml,
  buildUserInfoHtml,
  buildTotalsBarHtml,
  buildFlowHtml,
  buildContextCardHtml,
  buildContextColapseHtml,
  buildContextRowHtml,
  buildContextQrCodeScript,
} from './core'

export const buildExportHtmlDocument = (options = {}) => {
  const title = options.title || 'Relatório de Dados';
  const includeFACss = options.includeFontAwesomeCss !== false;
  const faCssUrl = options.faCssUrl || 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.4/css/all.min.css';
  const maxCellWidthPx = typeof options.maxCellWidthPx === 'number' ? options.maxCellWidthPx : 100;
  const reportCssUrl = options.reportCssUrl || (typeof window !== 'undefined' ? `${window.location.origin}/report-export.css` : '/report-export.css');
  const includeAppCss = options.includeAppCss !== false;
  const includeGoogleFontsCss = options.includeGoogleFontsCss !== false;
  const googleFontsUrl = options.googleFontsUrl || 'https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap';

  const appCssLinks = includeAppCss && typeof window !== 'undefined'
    ? (() => {
        try {
          // Links externos
          const hrefs = Array.from(document.querySelectorAll('link[rel="stylesheet"]'))
            .map(l => l && l.href)
            .filter(Boolean);
          const unique = Array.from(new Set(hrefs));
          const linksStr = unique
            .map(href => {
              try { return new URL(href, window.location.origin).href; } catch { return href; }
            })
            .map(abs => `<link rel="stylesheet" href="${abs}" />`)
            .join('\n');

          // Estilos inline (ex.: style-loader em dev)
          const styleTags = Array.from(document.querySelectorAll('style'))
            .map(s => s && s.textContent)
            .filter(css => css && css.trim().length > 0);
          const inlineStr = styleTags.length ? `<style>\n${styleTags.join('\n\n')}\n</style>` : '';

          return `${linksStr}\n${inlineStr}`;
        } catch (_) { return ''; }
      })()
    : '';

  const transactionCode = options.transactionCode || '';
  const columnsList = options.columns || [];
  const dataResult = options.filteredData || [];

  const exportColumns = columnsList.filter((col) =>
    (col.Export !== 'False' && col.Export !== false)
  );

  const headers = exportColumns.map((c) => {
    const w = typeof c.width === 'number' ? `${c.width}px` : c.width;
    const widthStyle = w ? ` style="width: ${w}; max-width: ${w}; --cell-max-width: ${w};"` : '';
    return `<th${widthStyle}>${escapeHtml(c.header)}</th>`;
  }).join('');
  const rows = dataResult.map((row) => {
    const tds = exportColumns.map((col) => {
      const content = renderExportTableCellHtml(row[col.field], col);
      const tdClass = 'cell';
      const w = typeof col.width === 'number' ? `${col.width}px` : col.width;
      const widthStyle = w ? ` style="width: ${w}; max-width: ${w}; --cell-max-width: ${w};"` : '';
      return `<td class="${tdClass}"${widthStyle}>${content}</td>`;
    }).join('');
    return `<tr>${tds}</tr>`;
  }).join('\n');

  const headerFooterHtml = buildReportHeaderHtml({ title });

  const filtersBarHtml = buildFiltersBarHtml({ appliedFilters: options.appliedFilters, transactionCode });

  const { html: userInfoHtml, userInfo } = buildUserInfoHtml(options);

  const flowHtml = buildFlowHtml(options.flowData);
  const contextCardHtml = buildContextCardHtml(options.contextData);
  const { html: contextColapseHtml, qrCodeItems: contextQrCodeItems } = buildContextColapseHtml(
    options.contextColapseData,
    options.enableContextColapseQRCode === true,
  );
  const contextQrCodeScript = buildContextQrCodeScript(contextQrCodeItems);
  const contextRowHtml = buildContextRowHtml({ flowHtml, contextCardHtml, contextColapseHtml });

  const totalsBarHtml = buildTotalsBarHtml(dataResult.length);
  const formatNowBr = () => {
    try {
      const d = new Date();
      const pad = (n) => String(n).padStart(2, '0');
      const dia = pad(d.getDate());
      const mes = pad(d.getMonth() + 1);
      const ano = d.getFullYear();
      const hora = pad(d.getHours());
      const min = pad(d.getMinutes());
      return `${dia}/${mes}/${ano} ${hora}:${min}`;
    } catch (_) { return ''; }
  };
  const footerHtml = `
    <div class="defender-footer">
      <div class="footer-left">Emitido por: <span> ${escapeHtml(userInfo?.nome || '')} às ${formatNowBr()} </span> </div>
      <div class="footer-right"></div>
    </div>
  `;

  const html = `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(title)}</title>
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <style>
    :root{--cell-max-width: ${maxCellWidthPx}px;} 
    html, body{font-family: "Poppins", sans-serif;} 
    td.cell{padding-top:10px;padding-bottom:10px;}
    .icon-cell {
      display: inline-flex !important;
      align-items: center !important;
      gap: 6px !important;
    }
    .icon-cell i {
      display: inline-block !important;
    }
  </style>
  <link rel="stylesheet" href="${reportCssUrl}" />
  ${includeGoogleFontsCss ? `<link rel="stylesheet" href="${googleFontsUrl}" />` : ''}
  ${includeFACss ? `<link rel="stylesheet" href="${faCssUrl}" crossorigin="anonymous" referrerpolicy="no-referrer" />` : ''}
  ${appCssLinks}
  </head>
<body>
  ${headerFooterHtml}
  ${filtersBarHtml}
  ${userInfoHtml}
  <div class="main-content px-5">
    ${contextRowHtml}

    ${options.tableTitle ? `<span class="font-w-600 text-color-azul2 font-size-16">${options.tableTitle}</span>` : ''}
    <table class="table-pdf-export mt-1">
      <thead><tr>${headers}</tr></thead>
      <tbody>
        ${rows}
      </tbody>
    </table>
    ${totalsBarHtml}
  </div>
  ${footerHtml}
  ${contextQrCodeScript}
</body>
</html>`;

  return html;
};

export const exportGridToHTML = async (options = {}) => {
  const filename = options.filename || 'relatorio_dados.html';
  const html = buildExportHtmlDocument(options);

  // Se convertToPdf for false, retornar HTML diretamente
  const convertToPdf = options.convertToPdf !== false // default é true para manter compatibilidade

  if (!convertToPdf) {
    // Fazer download do HTML diretamente
    const blob = new Blob([html], { type: 'text/html;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', filename)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
    return
  }

  // Gerar PDF via API
  try {
    // Obter CSRF token
    let csrfToken
    try {
      const csrfResponse = await BizinBackend.getCsrfToken()
      csrfToken = csrfResponse?.csrfToken || csrfResponse
    } catch (error) {
      const response = await axios.get(`${config.URL_SERVER}/_get_csrf_token`, {
        headers: { 'Origin': window.location.origin }
      })
      csrfToken = response.data?.csrfToken
    }

    if (!csrfToken) {
      throw new Error('Não foi possível obter CSRF token')
    }

    // Salvar HTML temporariamente
    const saveResponse = await axios.post(
      `${config.URL_SERVER}/salvar-html-temporario`,
      { html },
      {
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        timeout: 30000
      }
    )
    
    const tempUrl = saveResponse.data.url
    
    const response = await axios.post(
      `${config.URL_SERVER}/gerar-pdf`,
      { 
        parameters: {
          url: tempUrl
        }
      },
      {
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        responseType: 'blob',
        timeout: 180000
      }
    )

    // Verificar se recebeu PDF válido
    const contentType = response.headers['content-type'] || response.headers['Content-Type']
    
    // Verificar se é PDF pelo Content-Type
    const isPdfContentType = contentType === 'application/pdf' || contentType?.includes('pdf')
    
    const hasData = response.data instanceof Blob && response.data.size > 0
    
    let hasPdfSignature = false
    if (response.data instanceof Blob) {
      const firstBytes = await response.data.slice(0, 4).text()
      hasPdfSignature = firstBytes.startsWith('%PDF')
    }
    
    const isPdf = isPdfContentType && hasData && hasPdfSignature
    
    if (response.data && isPdf) {
      const pdfBlob = response.data instanceof Blob 
        ? response.data 
        : new Blob([response.data], { type: 'application/pdf' })
      
      const link = document.createElement('a')
      const url = URL.createObjectURL(pdfBlob)
      const pdfFilename = filename.endsWith('.pdf') ? filename : filename.replace(/\.html?$/, '.pdf')
      link.setAttribute('href', url)
      link.setAttribute('download', pdfFilename)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } else {
      throw new Error(`Resposta da API não é um PDF válido. Content-Type: ${contentType}, Status: ${response.status}`)
    }
  } catch (error) {
    throw new Error(`Erro ao gerar PDF: ${error.message}`)
  }
};

export default exportGridToHTML


